import { z } from 'zod';
import { GroupLabelSchema, normalizeGroupLabel } from './document-group-label.js';
import { DOCUMENT_LINK_CONTEXTS, LINK_CONTEXTS, INTAKE_CLASSES, DOCUMENT_STATUSES, DOCUMENT_HEALTH, VISIBILITY_VALUES, } from '../constants.js';
import { PaginationQuerySchema, ResourcePermissionsSchema, stringArrayQuery, BooleanQuerySchema, deletedFilterFields, refineDeletedFilterMutualExclusion, } from './common.js';
// ctsk-062 — document↔document link contexts. Used by POST/DELETE on
// /documents/:id/links to validate the requested verb.
export const DocumentLinkContextSchema = z.enum(DOCUMENT_LINK_CONTEXTS);
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const DocumentLinkCreateSchema = z.object({
    targetId: z.string().min(1).max(64),
    context: DocumentLinkContextSchema.default('related'),
    note: z.string().max(1000).nullable().optional(),
}).strict();
// 0.1.21 (Stage 1 of document-relationships FR) — declarative
// links carried in DocumentIngestMetaSchema.links[]. Each entry
// names ONE target resolver; the ingest route tries them in
// the documented precedence (document_id → external_id → url →
// crawl_match). Unresolved entries queue in
// `document_links_pending` for the reconciler.
export const IngestLinkTargetCrawlMatchSchema = z.object({
    /** UUID of the crawl run (carried in `crawl_job.id` /
     *  `crawl_job_id` CP). All target candidates must share this. */
    crawl_job_id: z.string().min(1).max(128),
    /** Producer-side page id within the crawl
     *  (`crawl_page.page_id` / `crawl_page_id` CP, e.g. "p0001"). */
    page_id: z.string().min(1).max(128),
});
export const IngestLinkSchema = z.object({
    /** Direct CT document id — strongest signal, used first. */
    targetDocumentId: z.string().min(1).max(64).optional(),
    /** Lookup by `documents.source.url` within the active org. */
    targetUrl: z.string().url().optional(),
    /** Crawl-tree resolver: lookup by joining on the
     *  `crawl_job_id` + `crawl_page_id` custom properties. */
    targetCrawlMatch: IngestLinkTargetCrawlMatchSchema.optional(),
    /** Relationship verb — must be a valid DOCUMENT_LINK_CONTEXTS
     *  value. The ingest route also inserts the inverse
     *  (DOCUMENT_LINK_MIRROR) for symmetry. */
    context: DocumentLinkContextSchema,
    /** Optional human note stored on the link row. */
    note: z.string().max(500).optional(),
}).refine((v) => [v.targetDocumentId, v.targetUrl, v.targetCrawlMatch]
    .filter((x) => x !== undefined).length === 1, { message: 'links[].* must carry exactly one of targetDocumentId / targetUrl / targetCrawlMatch' });
export const IntakeClassSchema = z.enum(INTAKE_CLASSES);
export const DocumentStatusSchema = z.enum(DOCUMENT_STATUSES);
export const DocumentHealthSchema = z.enum(DOCUMENT_HEALTH);
export const DocumentSourceSchema = z.object({
    type: z.enum(['external_url', 'local_import', 'web_clipper', 'manual', 'scratchpad', 'unknown']),
    url: z.string().url().nullable().optional(),
    /**
     * Optional provenance back-link to the raw upstream artifact this
     * document was derived from. Used by the report_generator → CT chain
     * to record where the source `audit_<domain>.json` lives in
     * hyper-scanno (`X-Hyper-Scanno-Audit-URL` header at the producer
     * → middle leg). Without this field, the back-link is silently
     * stripped by the schema's default strip-unknown-keys behavior.
     * Combine with a ctsk-062 document-link (`references` verb) when the
     * source artifact itself is also a CT document — `auditUrl` is the
     * fallback for upstream artifacts CT does not hold.
     */
    auditUrl: z.string().url().nullable().optional(),
    publisher: z.string().nullable().optional(),
    author: z.string().nullable().optional(),
    published: z.string().datetime().nullable().optional(),
    captured: z.string().datetime().nullable().optional(),
    importedFrom: z.string().nullable().optional(),
    importBatch: z.string().nullable().optional(),
    authority: z.enum(['primary', 'secondary', 'personal', 'unknown']),
});
export const DocumentClassificationSchema = z.object({
    domain: z.array(z.string()),
    platform: z.array(z.string()),
    function: z.array(z.string()),
    cognitive: z.array(z.string()),
    project: z.array(z.string()),
    system: z.array(z.string()),
    service: z.array(z.string()),
});
/** Body for PATCH /documents/:id — partial update. */
export const DocumentPatchSchema = z.object({
    title: z.string().min(1).max(500).optional(),
    status: DocumentStatusSchema.optional(),
    intakeClass: IntakeClassSchema.optional(),
    health: DocumentHealthSchema.optional(),
    date: z.string().datetime().nullable().optional(),
    archiveSerialNumber: z.number().int().nullable().optional(),
    classification: DocumentClassificationSchema.partial().optional(),
    customProperties: z.record(z.unknown()).optional(),
    source: DocumentSourceSchema.partial().optional(),
    permissions: ResourcePermissionsSchema.optional(),
    tagIds: z.array(z.string()).optional(),
    /**
     * Replace the document's body. Allowed only for textual documents
     * (text/markdown, text/plain, text/html). For binary types (PDF, image,
     * Word) the API returns 422 — re-upload instead. When set, the server
     * writes the new content to the same storagePath and bumps contentHash
     * + updatedAt.
     */
    content: z.string().max(1_000_000).optional(),
    /**
     * Cross-org visibility. PATCH'ing this field requires `documents:share`;
     * the route layer enforces. See VISIBILITY_VALUES in constants.ts.
     */
    visibility: z.enum(VISIBILITY_VALUES).optional(),
    /**
     * CTSK-498 — optional free-text labels (migration 0067). `null`, "" and
     * whitespace-only clear the label; an absent key leaves it unchanged. Run
     * through the shared normaliser (trim, NFC, 1-120 chars, no control / bidi /
     * zero-width characters). The route DROPS both fields silently unless
     * document grouping is enabled for the caller's org (env + per-org flag).
     */
    groupName: GroupLabelSchema.optional(),
    collectionName: GroupLabelSchema.optional(),
});
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const DocumentCreateSchema = z.object({
    title: z.string().min(1).max(500),
    intakeClass: IntakeClassSchema.default('structured_note'),
    mimeType: z.string(),
    sourcePath: z.string(),
    source: DocumentSourceSchema,
    content: z.string().optional(),
}).strict();
export const DocumentListQuerySchema = PaginationQuerySchema.extend({
    q: z.string().optional(),
    tag: stringArrayQuery(z.string()).optional(),
    containerId: z.string().optional(),
    intakeClass: stringArrayQuery(IntakeClassSchema).optional(),
    status: stringArrayQuery(DocumentStatusSchema).optional(),
    health: stringArrayQuery(DocumentHealthSchema).optional(),
    ownerId: z.string().optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    sort: z.enum(['title', 'createdAt', 'updatedAt', 'date']).default('updatedAt').optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
    // CTSK-388 (Phase 2) — deletedOnly added alongside the pre-existing
    // includeDeleted. Mutual-exclusion enforced by the .superRefine() below.
    ...deletedFilterFields,
    // ctsk-051 — narrow to docs the caller does (`me`) or does not
    // (`not-me`) watch. Mirrors the work-items ?watchedBy contract from
    // #112 so SDK / hyper-scanno consumers can read the same param name.
    watchedBy: z.enum(['me', 'not-me']).optional(),
    // FR 2026-05-29 — cross-org list. When set to 'all-my-orgs' AND
    // combined with watchedBy=me, the route fans out across every org
    // the caller is a member of (instead of the default active-org
    // scope), and stamps each row with its org. Other combinations
    // (e.g. scope=all-my-orgs without watchedBy=me) are rejected as
    // 422 — the cross-org surface is only for caller-scoped filters.
    scope: z.enum(['active-org', 'all-my-orgs']).optional(),
    // 0.1.23 (Stage 2b of document-relationships FR) — narrow the
    // list to docs linked FROM the given source document via the
    // document_links table. Combined with `relatedContext` (optional)
    // for a specific verb filter. The link table covers all 12
    // contexts so e.g. ?related=<seedId>&relatedContext=parent_of
    // returns the seed's direct crawl-page children.
    related: z.string().min(1).max(64).optional(),
    relatedContext: DocumentLinkContextSchema.optional(),
    // CTSK-498 — case-insensitive exact match on the group / collection label
    // (lower(col) = lower($1), served by the partial indexes in migration 0067).
    // Trimmed + NFC-normalised so a decomposed query matches a composed label.
    // Silently IGNORED (full unfiltered list) unless document grouping is
    // enabled for the caller's org.
    group: z.string().trim().max(120).transform((v) => v.normalize('NFC')).optional(),
    collection: z.string().trim().max(120).transform((v) => v.normalize('NFC')).optional(),
    // CTSK-500 — exact match on documents.ingest_batch_id (migration 0068): the
    // files dropped / picked together in one action. Same gate as group /
    // collection: silently IGNORED unless document grouping is enabled for the
    // caller's org. Deliberately NOT format-validated here (never a 400): the
    // route runs normalizeIngestBatchId and a malformed value matches nothing.
    ingestBatch: z.string().trim().max(64).optional(),
    // CTSK-501 — folder capture (migration 0069). `folder` is an exact match on
    // documents.source_folder; with `includeSubfolders=true` it also matches
    // `folder/%`. Compared as stored after NFC, CASE-SENSITIVE (unlike group).
    // Same gate as group / collection / ingestBatch: silently IGNORED (full
    // list) unless document grouping is enabled for the caller's org. Run
    // through normalizeSourceFolder in the route; a value it rejects matches
    // nothing (never a 400). Deliberately NOT format-validated here.
    folder: z.string().max(1100).optional(),
    includeSubfolders: BooleanQuerySchema.optional(),
}).superRefine(refineDeletedFilterMutualExclusion);
export const BulkDocumentOpSchema = z.object({
    ids: z.array(z.string()).min(1).max(1000),
    op: z.enum(['patch', 'delete', 'restore', 'tag_add', 'tag_remove', 'reprocess']),
    payload: z.unknown().optional(),
});
// ─── CTSK-498 — POST /documents/bulk-edit ───────────────────────────────
// Spec: docs/proposals/2026-10-16-document-bulk-edit.md §5.
/** Hard cap on ids per bulk-edit request (also the single-statement UPDATE bound). */
export const DOCUMENT_BULK_EDIT_MAX_IDS = 1000;
// ─── CTSK-518 — POST /documents/bulk-archive + /documents/bulk-purge ─────
/**
 * Body for the Document Library lifecycle batch routes (the batch twins of
 * DELETE /documents/:id and DELETE /documents/:id?force=true). Shares the
 * bulk-edit id cap. Strict: an unknown key is a 400 VALIDATION_ERROR that
 * names it.
 */
export const DocumentBulkIdsSchema = z
    .object({
    ids: z.array(z.string().min(1)).min(1).max(DOCUMENT_BULK_EDIT_MAX_IDS),
    /**
     * false (default) = partial settlement. true = stop at the FIRST row
     * that fails and report every later row as SKIPPED; rows already
     * written stay written. Same semantics as POST /documents/bulk-edit.
     */
    stopOnError: z.boolean().default(false),
})
    .strict();
export const BulkEditStrategySchema = z.enum(['REPLACE', 'CLEAR', 'NO_CHANGE']);
/**
 * One label's mutation. `strategy` omitted or NO_CHANGE = ignored (a stray
 * `value` is ignored too). REPLACE needs a `value` that survives the shared
 * normaliser as a NON-empty label — use CLEAR to remove one. Strict (CTSK-445):
 * an unknown key is a 400 VALIDATION_ERROR that names it.
 */
export const BulkLabelMutationSchema = z
    .object({
    strategy: BulkEditStrategySchema.optional(),
    value: z.string().optional(),
})
    .strict()
    .superRefine((m, ctx) => {
    if (m.strategy !== 'REPLACE')
        return;
    const r = normalizeGroupLabel(m.value);
    if (!r.ok) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value'], message: r.message });
    }
    else if (r.value === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'REPLACE requires a non-empty value; use CLEAR to remove the label.',
        });
    }
});
// ─── CTSK-517 — Stage 2 palette: tags, container, customProperties, frontmatter, workItemLinks ───
//
// Same REPLACE / CLEAR / NO_CHANGE top-level strategy as the label fields.
// Every object is strict (CTSK-445). References are by NAME / KEY, resolved
// once per request in the route (never per row). CLEAR with REPLACE-only
// fields present, and REPLACE without its required payload, are 400s.
const BULK_REF_MAX = 200;
const refName = z.string().trim().min(1).max(BULK_REF_MAX);
/** `tags`: REPLACE + mode (default 'replace' = overwrite the full set; 'add' / 'remove' touch only the named tags). CLEAR removes every tag. */
export const BulkTagsMutationSchema = z
    .object({
    strategy: BulkEditStrategySchema.optional(),
    mode: z.enum(['replace', 'add', 'remove']).optional(),
    names: z.array(refName).max(100).optional(),
})
    .strict()
    .superRefine((m, ctx) => {
    if (m.strategy === 'REPLACE') {
        const mode = m.mode ?? 'replace';
        if (!m.names) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['names'], message: 'REPLACE requires names (an empty list with mode "replace" sets an empty tag set).' });
        }
        else if (mode !== 'replace' && m.names.length === 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['names'], message: `mode "${mode}" requires at least one tag name.` });
        }
    }
    else if (m.strategy === 'CLEAR' && (m.mode !== undefined || m.names !== undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['names'], message: 'CLEAR takes no mode or names; it removes every tag.' });
    }
});
/** `container`: REPLACE assigns exactly the named container (any other container membership is removed). CLEAR removes all memberships. */
export const BulkContainerMutationSchema = z
    .object({
    strategy: BulkEditStrategySchema.optional(),
    name: refName.optional(),
})
    .strict()
    .superRefine((m, ctx) => {
    if (m.strategy === 'REPLACE' && !m.name) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['name'], message: 'REPLACE requires a container name; use CLEAR to remove the assignment.' });
    }
    if (m.strategy === 'CLEAR' && m.name !== undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['name'], message: 'CLEAR takes no name.' });
    }
});
/** Scalar value of a custom property. null is not allowed — remove a CP with CLEAR. */
const BulkCpValueSchema = z.union([z.string().max(10_000), z.number().finite(), z.boolean()]);
/**
 * `customProperties` (keyed by CP `key`): REPLACE sets the listed CPs and leaves the rest alone
 * (per-CP merge); `replaceAll: true` also removes every CP not listed. CLEAR removes the CPs in
 * `names`, or ALL of them when `names` is absent.
 */
export const BulkCustomPropertiesMutationSchema = z
    .object({
    strategy: BulkEditStrategySchema.optional(),
    values: z.record(z.string().min(1).max(64), BulkCpValueSchema).optional(),
    replaceAll: z.boolean().optional(),
    names: z.array(z.string().min(1).max(64)).max(100).optional(),
})
    .strict()
    .superRefine((m, ctx) => {
    if (m.strategy === 'REPLACE') {
        if (!m.values || (Object.keys(m.values).length === 0 && !m.replaceAll)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['values'], message: 'REPLACE requires at least one value (or replaceAll with an empty map to wipe).' });
        }
        if (m.names !== undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['names'], message: 'REPLACE takes values, not names.' });
        }
    }
    else if (m.strategy === 'CLEAR' && (m.values !== undefined || m.replaceAll !== undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['values'], message: 'CLEAR takes only an optional names list.' });
    }
});
/**
 * `frontmatter` (top-level keys of the sidecar's `frontmatter` object — flat, no deep merge):
 * REPLACE merges `values` per key (un-named keys preserved); `mode: 'replace'` swaps the whole
 * object. CLEAR removes the keys in `keys`, or the whole object when `keys` is absent.
 */
export const BulkFrontmatterMutationSchema = z
    .object({
    strategy: BulkEditStrategySchema.optional(),
    mode: z.enum(['merge', 'replace']).optional(),
    values: z.record(z.string().min(1).max(128), z.unknown()).optional(),
    keys: z.array(z.string().min(1).max(128)).max(100).optional(),
})
    .strict()
    .superRefine((m, ctx) => {
    if (m.strategy === 'REPLACE') {
        if (!m.values || (Object.keys(m.values).length === 0 && m.mode !== 'replace')) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['values'], message: 'REPLACE requires at least one key (an empty map is only valid with mode "replace").' });
        }
        if (m.keys !== undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['keys'], message: 'REPLACE takes values, not keys.' });
        }
    }
    else if (m.strategy === 'CLEAR' && (m.values !== undefined || m.mode !== undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['values'], message: 'CLEAR takes only an optional keys list.' });
    }
});
/**
 * `workItemLinks`: `context` is REQUIRED for REPLACE and CLEAR ("all contexts" is not a strategy).
 * REPLACE makes the named work items (keys such as CTSK-123) the document's links for THAT context
 * only; other contexts are untouched. CLEAR removes the context's links (only the listed work
 * items when `workItems` is given).
 */
export const BulkWorkItemLinksMutationSchema = z
    .object({
    strategy: BulkEditStrategySchema.optional(),
    context: z.enum(LINK_CONTEXTS).optional(),
    workItems: z.array(refName).max(100).optional(),
})
    .strict()
    .superRefine((m, ctx) => {
    if (m.strategy !== 'REPLACE' && m.strategy !== 'CLEAR')
        return;
    if (!m.context) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['context'], message: `${m.strategy} requires a link context.` });
    }
    if (m.strategy === 'REPLACE' && (!m.workItems || m.workItems.length === 0)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['workItems'], message: 'REPLACE requires at least one work item key; use CLEAR to remove links.' });
    }
});
export const DocumentBulkEditSchema = z
    .object({
    ids: z.array(z.string().min(1)).min(1).max(DOCUMENT_BULK_EDIT_MAX_IDS),
    mutations: z
        .object({
        groupName: BulkLabelMutationSchema.optional(),
        collectionName: BulkLabelMutationSchema.optional(),
        // CTSK-517 — Stage 2 palette.
        tags: BulkTagsMutationSchema.optional(),
        container: BulkContainerMutationSchema.optional(),
        customProperties: BulkCustomPropertiesMutationSchema.optional(),
        frontmatter: BulkFrontmatterMutationSchema.optional(),
        workItemLinks: BulkWorkItemLinksMutationSchema.optional(),
    })
        .strict(),
    /**
     * false (default) = partial settlement. true = stop at the FIRST row that
     * fails the per-row pre-flight check (not found / wrong org / no edit
     * right) and report every later row as SKIPPED. Rows already cleared
     * before the stop ARE applied, exactly as POST /workitems/bulk behaves.
     */
    stopOnError: z.boolean().default(false),
})
    .strict();
/** The five Stage 2 fields, in the fixed order the route applies them (audit is per row, so order is for determinism only). */
export const BULK_EDIT_STAGE2_FIELDS = ['tags', 'container', 'customProperties', 'frontmatter', 'workItemLinks'];
/**
 * Collapse parsed mutations to the changes that actually apply. Empty object
 * = nothing to do (the route answers 422 NO_EFFECTIVE_MUTATION). Run AFTER
 * DocumentBulkEditSchema.parse, which already guarantees REPLACE values are
 * valid and non-empty.
 */
export function effectiveBulkEdit(mutations) {
    const out = {};
    for (const field of ['groupName', 'collectionName']) {
        const m = mutations[field];
        if (!m?.strategy || m.strategy === 'NO_CHANGE')
            continue;
        if (m.strategy === 'CLEAR') {
            out[field] = { kind: 'clear' };
            continue;
        }
        const r = normalizeGroupLabel(m.value);
        if (r.ok && r.value !== null)
            out[field] = { kind: 'set', value: r.value };
    }
    for (const field of BULK_EDIT_STAGE2_FIELDS) {
        const m = mutations[field];
        if (!m?.strategy || m.strategy === 'NO_CHANGE')
            continue;
        // Parsed + validated by DocumentBulkEditSchema; the union of five shapes is assigned per key.
        out[field] = m;
    }
    return out;
}
/**
 * Metadata payload for POST /documents/ingest — the trusted "runner
 * ingest" path. Sent as a JSON string in the multipart `meta` field
 * alongside the `file` part. Unlike DocumentCreate (a metadata-only
 * row) this rides with the actual bytes and lets an external tool
 * (e.g. report_generator) push a *finished, classified, approved*
 * artifact in one call: it lands tagged + routed + stamped, runs the
 * nest's workflows/rules, queues enrichment for indexing, and emits
 * realtime events.
 *
 * Every field is optional — a bare `{}` behaves like a plain upload
 * that defaults to an approved (`reviewed`) status.
 *
 * Strict (CTSK-445) — unknown TOP-LEVEL keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop). External producers (RG,
 * hyper-scanno, tootasko-clipper) were audited against their documented
 * payloads (docs/integrations/*) — all within schema. Arbitrary
 * producer metadata belongs in `frontmatter` (a permissive record),
 * NOT as new top-level keys.
 */
export const DocumentIngestMetaSchema = z.object({
    /** Override the title (defaults to the filename without extension). */
    title: z.string().min(1).max(500).optional(),
    /** Tags to apply, by tag id OR case-insensitive name. Applied at ingest
     *  step 8.5, before file-workflows and tagging rules — union guaranteed
     *  with any rule/workflow-derived tags (CTSK-439/441 ordering). */
    tags: z.array(z.string().min(1).max(200)).max(50).optional(),
    /** Create any named tag that doesn't exist yet (default: false →
     *  unknown names are a 422 so the taxonomy stays curated). */
    createMissingTags: z.boolean().default(false),
    /** Target storage container, by id OR case-insensitive name. Applied
     *  LAST and replaces any container set by default-intake, so explicit
     *  routing wins (the ctsk-074 concern). */
    container: z.string().min(1).max(200).optional(),
    /** Intake classification (defaults to structured_note for textual,
     *  external_import otherwise). */
    intakeClass: IntakeClassSchema.optional(),
    /** Landing status. Defaults to `reviewed` (= approved) because the
     *  runner is a trusted producer of a finished artifact. Pass
     *  `captured` to drop it into the normal review pipeline instead. */
    status: DocumentStatusSchema.default('reviewed'),
    /** Health. Defaults to `healthy` for an approved ingest. */
    health: DocumentHealthSchema.default('healthy'),
    /** Provenance stamp. Defaults to a tool source. */
    source: DocumentSourceSchema.partial().optional(),
    /** Seed values for sidecar.frontmatter. Written BEFORE workflows
     *  and on_import tagging rules fire, so rules using metadata_field
     *  can read these values on the first pipeline tick. Workflow
     *  set_frontmatter actions extend / per-field override (workflow
     *  runs after seeding).
     *
     *  Producers shipping raw JSON / binary files that can't carry
     *  inline YAML frontmatter use this to project a structured
     *  metadata header so the tagging-rule layer still has a populated
     *  source to read from. See docs/documents-ingest-api.md (§
     *  "meta.frontmatter") and docs/integrations/hyperscanno-direct.md
     *  §5.2. FR 2026-06-01 — projects schema-headed JSON. */
    frontmatter: z.record(z.unknown()).optional(),
    /** Run upload-time file workflows + on_import tagging rules
     *  (default: true). */
    runWorkflows: z.boolean().default(true),
    /** Queue the metadata-enrichment pipeline for indexing/summary/
     *  keyphrases (default: true). */
    enrich: z.boolean().default(true),
    /** 0.1.21 (Stage 1 of document-relationships FR) — declarative
     *  document-to-document links to create at ingest time.
     *
     *  Each entry must carry EXACTLY ONE target resolver
     *  (`targetDocumentId` / `targetExternalId` / `targetUrl` /
     *  `targetCrawlMatch`). The ingest route tries them in that order;
     *  the first that resolves wins.
     *
     *  Crawls produce many docs in parallel; a child page may land
     *  BEFORE its parent. Unresolved links are queued in
     *  `document_links_pending` with a 24h TTL and a background
     *  reconciler retries every 5 min — see
     *  `apps/api/src/workers/document-link-reconciler.ts`.
     *
     *  Inverse rows: the ingest path inserts BOTH directions
     *  (forward + the `DOCUMENT_LINK_MIRROR` inverse) so navigating
     *  "find my parent" doesn't require a `WHERE to = X` reverse
     *  query.
     */
    links: z.array(IngestLinkSchema).max(200).optional(),
}).strict();
//# sourceMappingURL=document.js.map